import React, { useState } from 'react';
import { Table } from '../../Component/Table';
import { useTranslation } from 'react-i18next';
import { Button, Space, Popconfirm, message, Tag, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Api from '../../Network';
import UserModal from './UserModal';
import Can from '../../Component/Can';
import NameByLang from '../../Component/nameByLang';
import { AnimatedContainer } from '../../Component/ui';
import './style.scss';

const Users = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (userId: number) => Api.delete(`/api/admin/users/${userId}`),
    onSuccess: () => {
      message.success(t('users.deleteSuccess', 'User deleted successfully'));
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.msg || t('common.error', 'Error deleting user');
      message.error(errorMessage);
    },
  });

  const handleDelete = (userId: number) => {
    deleteUserMutation.mutate(userId);
  };

  const handleEdit = (record: any) => {
    setSelectedUser(record);
    setModalMode('edit');
    setModalVisible(true);
  };

  const handleAddNew = () => {
    setSelectedUser(null);
    setModalMode('create');
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedUser(null);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
  };

  return (
    <AnimatedContainer className="users-page" variant="fadeUp">
      <Table
        tableQueryKey="/api/admin/users"
        tableTitle={t('users.title', 'Users Management')}
        showSearch={true}
        columns={[
          {
            title: t('users.columns.name', 'Name'),
            dataIndex: 'name',
            key: 'name',
            render: (name: string) => (
              <span className="bold primary">
                <UserOutlined style={{ marginRight: 8 }} />
                {name}
              </span>
            ),
          },
          {
            title: t('users.columns.email', 'Email'),
            dataIndex: 'email',
            key: 'email',
          },
          {
            title: t('users.columns.phone', 'Phone'),
            dataIndex: 'phone_number',
            key: 'phone_number',
          },
          {
            title: t('users.columns.roles', 'Roles'),
            key: 'roles',
            render: (_: any, record: any) => (
              <Space wrap>
                {record.roles?.map((role: any) => (
                  <Tag key={role.id} color="blue">
                    <NameByLang
                      name_en={role.display_name_en}
                      name_ar={role.display_name_ar}
                      name={role.display_name || role.name}
                    />
                  </Tag>
                ))}
              </Space>
            ),
          },
          {
            title: t('users.columns.status', 'Status'),
            dataIndex: 'status',
            key: 'status',
            render: (status: boolean | string | null | undefined) => {
              if (status === null || status === undefined) {
                return <Tag color="default">-</Tag>;
              }
              const isActive =
                typeof status === 'string' ? status.toLowerCase().trim() === 'active' : !!status;
              return (
                <Tag color={isActive ? 'green' : 'red'}>
                  {isActive ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                </Tag>
              );
            },
          },
          {
            title: t('common.actions', 'Actions'),
            key: 'actions',
            width: 150,
            align: 'center',
            render: (_: any, record: any) => (
              <div className="users-row-actions">
                <Can permission="users.update">
                  <Tooltip title={t('common.edit', 'Edit')}>
                    <Button
                      type="default"
                      size="small"
                      className="users-table-btn"
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(record)}
                      aria-label={t('common.edit', 'Edit')}
                    />
                  </Tooltip>
                </Can>
                <Can permission="users.delete">
                  <Popconfirm
                    title={t('users.deleteConfirm', 'Are you sure you want to delete this user?')}
                    description={t('users.deleteWarning', 'This action cannot be undone.')}
                    onConfirm={() => handleDelete(record.id)}
                    okText={t('common.yes', 'Yes')}
                    cancelText={t('common.no', 'No')}
                  >
                    <Tooltip title={t('common.delete', 'Delete')}>
                      <Button
                        type="primary"
                        danger
                        size="small"
                        className="users-table-btn users-table-btn--danger"
                        icon={<DeleteOutlined />}
                        aria-label={t('common.delete', 'Delete')}
                      />
                    </Tooltip>
                  </Popconfirm>
                </Can>
              </div>
            ),
          },
        ]}
        buttonContent={t('users.addNew', 'Add New User')}
        onClickNewButton={handleAddNew}
        createPermission="users.create"
      />

      <UserModal
        visible={modalVisible}
        mode={modalMode}
        user={selectedUser}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </AnimatedContainer>
  );
};

export default Users;
